import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/contexts/UserContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Shield, Upload, FileText, Download, Eye, TrendingUp } from "lucide-react";
import { PolicyAcknowledgementStats } from "./PolicyAcknowledgementStats";

type PolicyType = "code_of_ethics" | "code_of_conduct" | "gdpr_policy";

export const CompanyPoliciesTab = () => {
  const { user } = useUser();
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [uploadingType, setUploadingType] = useState<PolicyType | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [customTabName, setCustomTabName] = useState("");

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      // @ts-ignore - admin_bubble_id exists but not in types yet
      const { data } = await supabase
        .from("profiles")
        .select("admin_bubble_id")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  // @ts-ignore - admin_bubble_id not in types yet
  const { data: policies = [], isLoading } = useQuery({
    // @ts-ignore
    queryKey: ["company-policies-admin", profile?.admin_bubble_id],
    queryFn: async () => {
      // @ts-ignore
      if (!profile?.admin_bubble_id) return [];

      // @ts-ignore - New table not in types yet
      const { data, error } = await supabase
        // @ts-ignore
        .from("company_policies")
        .select("*")
        // @ts-ignore
        .eq("admin_bubble_id", profile.admin_bubble_id)
        .eq("is_active", true)
        .order("policy_type");

      if (error) throw error;
      return data || [];
    },
    // @ts-ignore
    enabled: !!profile?.admin_bubble_id,
  });

  const uploadPolicyMutation = useMutation({
    mutationFn: async ({ file, policyType }: { file: File; policyType: PolicyType }) => {
      // @ts-ignore
      if (!user?.id || !profile?.admin_bubble_id) throw new Error("Not authenticated");

      // @ts-ignore
      const fileExt = file.name.split(".").pop();
      // @ts-ignore
      const fileName = `${profile.admin_bubble_id}/${policyType}_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("company-policies")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // @ts-ignore - Get the current version
      const { data: existingPolicy } = await supabase
        // @ts-ignore
        .from("company_policies")
        .select("version")
        // @ts-ignore
        .eq("admin_bubble_id", profile.admin_bubble_id)
        .eq("policy_type", policyType)
        .eq("is_active", true)
        .single();

      // @ts-ignore
      const newVersion = existingPolicy ? (existingPolicy.version || 0) + 1 : 1;

      // @ts-ignore - New table not in types yet
      // Deactivate old policy
      await supabase
        // @ts-ignore
        .from("company_policies")
        .update({ is_active: false })
        // @ts-ignore
        .eq("admin_bubble_id", profile.admin_bubble_id)
        .eq("policy_type", policyType);

      // @ts-ignore - New table not in types yet
      // Insert new policy with incremented version
      // @ts-ignore
      const { error: insertError } = await supabase.from("company_policies").insert({
        // @ts-ignore
        admin_bubble_id: profile.admin_bubble_id,
        policy_type: policyType,
        file_url: fileName,
        file_name: file.name,
        uploaded_by: user.id,
        version: newVersion,
        custom_tab_name: customTabName || null,
      });

      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-policies-admin"] });
      queryClient.invalidateQueries({ queryKey: ["company-policies"] });
      toast({
        title: t("success"),
        description: t("policyUploadedSuccessfully"),
      });
      setUploadingType(null);
      setCustomTabName("");
    },
    onError: (error: any) => {
      toast({
        title: t("error"),
        description: error.message,
        variant: "destructive",
      });
      setUploadingType(null);
    },
  });

  const handleFileUpload = async (file: File | null, policyType: PolicyType) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast({
        title: t("error"),
        description: "Only PDF files are allowed",
        variant: "destructive",
      });
      return;
    }

    setUploadingType(policyType);
    uploadPolicyMutation.mutate({ file, policyType });
  };

  const handleViewPolicy = async (policy: any) => {
    try {
      const { data, error } = await supabase.storage
        .from("company-policies")
        .createSignedUrl(policy.file_url, 3600);

      if (error) throw error;
      window.open(data.signedUrl, "_blank");
    } catch (error: any) {
      toast({
        title: t("error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDownloadPolicy = async (policy: any) => {
    try {
      const { data, error } = await supabase.storage
        .from("company-policies")
        .download(policy.file_url);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = policy.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      toast({
        title: t("error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getPolicyLabel = (policyType: string) => {
    switch (policyType) {
      case "code_of_ethics":
        return t("codeOfEthics");
      case "code_of_conduct":
        return t("codeOfConduct");
      case "gdpr_policy":
        return t("gdprPolicy");
      default:
        return policyType;
    }
  };

  const getPolicy = (type: PolicyType) => {
    // @ts-ignore
    return policies.find((p) => p.policy_type === type);
  };

  const renderPolicyCard = (policyType: PolicyType) => {
    const policy: any = getPolicy(policyType);

    return (
      <Card key={policyType}>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>{getPolicyLabel(policyType)}</CardTitle>
              <CardDescription>
                {policy
                  ? `${t("currentVersion")}: ${policy.version}`
                  : t("noActivePolicy")}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {policy && (
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={() => handleViewPolicy(policy)}>
                <Eye className="h-4 w-4 mr-2" />
                {t("viewPolicy")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownloadPolicy(policy)}
              >
                <Download className="h-4 w-4 mr-2" />
                {t("downloadPolicy")}
              </Button>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`tab-name-${policyType}`}>
                {t("customTabName")} ({t("optional")})
              </Label>
              <Input
                id={`tab-name-${policyType}`}
                type="text"
                placeholder={t("ethicsCompliance")}
                value={customTabName}
                onChange={(e) => setCustomTabName(e.target.value)}
                disabled={uploadingType === policyType}
              />
              <p className="text-xs text-muted-foreground">
                {t("customTabNameHelp")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`file-${policyType}`}>
                {policy ? t("uploadNewVersion") : t("uploadPolicy")}
              </Label>
              <Input
                id={`file-${policyType}`}
                type="file"
                accept=".pdf"
                onChange={(e) => handleFileUpload(e.target.files?.[0] || null, policyType)}
                disabled={uploadingType === policyType}
              />
              <p className="text-xs text-muted-foreground">
                {policy && t("uploadNewVersionNote")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // @ts-ignore
  if (showStats && profile?.admin_bubble_id) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-2xl font-bold">{t("policyStatistics")}</h2>
              <p className="text-muted-foreground">{t("viewPolicyStats")}</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setShowStats(false)}>
            {t("back")}
          </Button>
        </div>
        {/* @ts-ignore */}
        <PolicyAcknowledgementStats adminBubbleId={profile.admin_bubble_id} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold">{t("companyPolicies")}</h2>
            <p className="text-muted-foreground">
              {t("uploadPolicy")} - PDF files only
            </p>
          </div>
        </div>
        <Button onClick={() => setShowStats(true)}>
          <TrendingUp className="h-4 w-4 mr-2" />
          {t("viewPolicyStats")}
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {renderPolicyCard("code_of_ethics")}
        {renderPolicyCard("code_of_conduct")}
        {renderPolicyCard("gdpr_policy")}
      </div>
    </div>
  );
};
